import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import Aos from "aos";
import { motion } from "framer-motion";

const screenshots = [
    "https://ik.imagekit.io/arthgyan/Arthgyan_Web/App_Screens/Screenshot_1791824239.png",
    "https://ik.imagekit.io/arthgyan/Arthgyan_Web/App_Screens/Screenshot_1791824524.png",
    "https://ik.imagekit.io/arthgyan/Arthgyan_Web/App_Screens/Screenshot_1791824532.png",
    "https://ik.imagekit.io/arthgyan/Arthgyan_Web/App_Screens/Screenshot_1791824536.png",
    "https://ik.imagekit.io/arthgyan/Arthgyan_Web/App_Screens/Screenshot_1791824581.png",
];

const ANDROID_URL =
    "https://play.google.com/store/apps/details?id=com.arthgyan.financemanager";
const IOS_URL = "https://apps.apple.com/in/app/arthgyan/id6796249762";

const features = [
    { icon: <TrendingUpRoundedIcon fontSize="small" />, label: "Investments" },
    { icon: <ShieldOutlinedIcon fontSize="small" />, label: "Insurance" },
    {
        icon: <AccountBalanceWalletOutlinedIcon fontSize="small" />,
        label: "Financial Planning",
    },
];

const AUTOPLAY_MS = 3000;


const Eyebrow = ({ children }) => {
    const theme = useTheme();
    return (
        <Box
            data-aos="fade-up"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 1.75,
                py: 0.6,
                mb: 2,
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
        >
            <Box
                sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: theme.palette.primary.main,
                }}
            />
            {children}
        </Box>
    );
};

const GradientWord = ({ children }) => {
    const theme = useTheme();
    return (
        <Box
            component="span"
            sx={{
                fontWeight: 800,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light || theme.palette.primary.main} 60%, ${theme.palette.text.primary})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
            }}
        >
            {children}
        </Box>
    );
};

const headingSx = (theme) => ({
    fontSize: { xs: "26px", sm: "34px", md: "46px" },
    lineHeight: 1.15,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    color: theme.palette.text.primary,
});


const AppPreview = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery("(min-width:1024px)");
    const isTablet = useMediaQuery("(min-width:600px)");

    const [activeIndex, setActiveIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [platform, setPlatform] = useState("android");

    const total = screenshots.length;
    const primary = theme.palette.primary.main;

    // responsive sizing for the carousel
    const phoneWidth = isDesktop ? 210 : isTablet ? 170 : 140;
    const spread = isDesktop ? 1 : isTablet ? 0.75 : 0.5;
    const stageHeight = isDesktop ? 470 : isTablet ? 400 : 340;

    useEffect(() => {
        if (paused) return undefined;
        const id = setInterval(
            () => setActiveIndex((prev) => (prev + 1) % total),
            AUTOPLAY_MS
        );
        return () => clearInterval(id);
    }, [paused, total]);

    useEffect(() => {
        Aos.init({ duration: 900, once: true, easing: "ease-out-cubic" });
    }, []);

    const getOffset = useCallback(
        (index) => {
            const half = Math.floor(total / 2);
            return ((index - activeIndex + total + half) % total) - half;
        },
        [activeIndex, total]
    );

    const getCardState = (offset) => {
        const abs = Math.abs(offset);
        const dir = Math.sign(offset);
        if (abs === 0)
            return { x: 0, scale: 1.12, rotateY: 0, opacity: 1, zIndex: 50, blur: 0 };
        if (abs === 1)
            return {
                x: dir * 235 * spread,
                scale: 0.92,
                rotateY: -dir * 32,
                opacity: 0.85,
                zIndex: 40,
                blur: 0,
            };
        return {
            x: dir * 410 * spread,
            scale: 0.78,
            rotateY: -dir * 48,
            opacity: isDesktop ? 0.5 : 0,
            zIndex: 30,
            blur: 1.5,
        };
    };

    const isAndroid = platform === "android";
    const storeUrl = isAndroid ? ANDROID_URL : IOS_URL;
    const comingSoon = !storeUrl;

    return (
        <Box
            sx={{
                position: "relative",
                overflow: "hidden",
                py: { xs: 6, md: 10 },
                background: `linear-gradient(180deg, ${alpha(primary, 0.04)} 0%, transparent 45%, ${alpha(primary, 0.05)} 100%)`,
            }}
        >
            {/* soft background glow */}
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    top: { xs: 40, md: 60 },
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: { xs: 320, md: 760 },
                    height: { xs: 320, md: 760 },
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${alpha(primary, 0.22)} 0%, transparent 65%)`,
                    filter: "blur(40px)",
                    pointerEvents: "none",
                }}
            />

            <Box sx={{ position: "relative", textAlign: "center", px: 2 }}>
                <Typography
                    sx={{
                        fontSize: "13px",
                        letterSpacing: "1.5px",
                        fontWeight: 600,
                        mb: 1,
                        color: theme.palette.primary.main,
                    }}
                    data-aos="fade-right"
                >
                    • App Experience
                </Typography>



                <Typography
                    sx={{
                        fontSize: { xs: "20px", sm: "28px", md: "40px" },
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        position: "relative",
                        display: "inline-block",
                    }}
                >
                    Experience{" "}
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 900,
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.text.primary})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            position: "relative",
                            display: "inline-block",
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                bottom: "-4px",
                                left: 0,
                                width: "100%",
                                height: "2px",
                                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
                            },
                        }}
                    >
                        ArthGyan
                    </Box>
                </Typography>
                <Typography
                    data-aos="fade-up"
                    data-aos-delay="100"
                    sx={{
                        fontSize: { xs: 14, md: 17 },
                        lineHeight: 1.7,
                        color: "text.secondary",
                        maxWidth: 620,
                        mx: "auto",
                        mt: 2,
                    }}
                >
                    Manage investments, insurance, and financial planning — all in one
                    beautifully designed app.
                </Typography>

                {/* feature chips */}
                <Stack
                    direction="row"
                    flexWrap="wrap"
                    justifyContent="center"
                    gap={1.25}
                    sx={{ mt: 3 }}
                    data-aos="fade-up"
                    data-aos-delay="150"
                >
                    {features.map((f) => (
                        <Stack
                            key={f.label}
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            sx={{
                                px: 1.75,
                                py: 0.8,
                                borderRadius: 999,
                                fontSize: 13,
                                fontWeight: 600,
                                color: "text.primary",
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                                border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                                backdropFilter: "blur(8px)",
                                "& svg": { color: primary },
                            }}
                        >
                            {f.icon}
                            <span>{f.label}</span>
                        </Stack>
                    ))}
                </Stack>

                {/* 3D CAROUSEL */}
                <Box
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    sx={{
                        position: "relative",
                        width: "100%",
                        maxWidth: 1100,
                        height: stageHeight,
                        mx: "auto",
                        mt: { xs: 5, md: 7 },
                        perspective: "1600px",
                    }}
                >
                    {screenshots.map((src, index) => {
                        const offset = getOffset(index);
                        const s = getCardState(offset);
                        const isCenter = offset === 0;

                        return (
                            <motion.div
                                key={src}
                                onClick={() => setActiveIndex(index)}
                                initial={false}
                                animate={{
                                    x: s.x,
                                    scale: s.scale,
                                    rotateY: s.rotateY,
                                    opacity: s.opacity,
                                    filter: `blur(${s.blur}px)`,
                                }}
                                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    marginLeft: -phoneWidth / 2,
                                    bottom: 0,
                                    width: phoneWidth,
                                    zIndex: s.zIndex,
                                    cursor: isCenter ? "default" : "pointer",
                                    transformStyle: "preserve-3d",
                                }}
                            >
                                {/* phone frame */}
                                <Box
                                    sx={{
                                        borderRadius: `${phoneWidth * 0.17}px`,
                                        p: "6px",
                                        bgcolor: "#0f1115",
                                        boxShadow: isCenter
                                            ? `0 40px 80px -20px ${alpha(primary, 0.55)}, 0 20px 40px rgba(0,0,0,0.3)`
                                            : "0 25px 50px rgba(0,0,0,0.25)",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        transition: "box-shadow .6s ease",
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={src}
                                        alt={`ArthGyan app screen ${index + 1}`}
                                        loading="lazy"
                                        draggable={false}
                                        sx={{
                                            display: "block",
                                            width: "100%",
                                            height: "auto",
                                            borderRadius: `${phoneWidth * 0.14}px`,
                                            userSelect: "none",
                                        }}
                                    />
                                </Box>
                            </motion.div>
                        );
                    })}
                </Box>

                {/* dots */}
                <Stack
                    direction="row"
                    justifyContent="center"
                    spacing={1}
                    sx={{ mt: 4 }}
                >
                    {screenshots.map((_, i) => (
                        <Box
                            key={i}
                            role="button"
                            aria-label={`Show screen ${i + 1}`}
                            onClick={() => setActiveIndex(i)}
                            sx={{
                                height: 8,
                                width: i === activeIndex ? 26 : 8,
                                borderRadius: 999,
                                cursor: "pointer",
                                bgcolor:
                                    i === activeIndex
                                        ? primary
                                        : alpha(theme.palette.text.primary, 0.18),
                                transition: "all .35s ease",
                            }}
                        />
                    ))}
                </Stack>
            </Box>

            {/* ================= DOWNLOAD CTA ================= */}
            <Box
                data-aos="fade-up"
                sx={{
                    position: "relative",
                    mx: "auto",
                    mt: { xs: 8, md: 12 },
                    px: { xs: 3, md: 6 },
                    py: { xs: 5, md: 7 },
                    maxWidth: 860,
                    width: { xs: "calc(100% - 32px)", md: "100%" },
                    textAlign: "center",
                    borderRadius: { xs: "24px", md: "32px" },
                    overflow: "hidden",
                    color: "#fff",
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark || primary} 0%, ${primary} 55%, ${theme.palette.primary.light || primary} 100%)`,
                    boxShadow: `0 30px 70px -25px ${alpha(primary, 0.7)}`,
                }}
            >
                {/* decorative circles */}
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        top: -90,
                        right: -90,
                        width: 260,
                        height: 260,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.1)",
                    }}
                />
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        bottom: -110,
                        left: -80,
                        width: 280,
                        height: 280,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.07)",
                    }}
                />

                <Box sx={{ position: "relative" }}>
                    <Typography
                        sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: 2,
                            textTransform: "uppercase",
                            opacity: 0.85,
                            mb: 1.5,
                        }}
                    >
                        Get Started
                    </Typography>

                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: "26px", sm: "34px", md: "42px" },
                            fontWeight: 800,
                            lineHeight: 1.15,
                            letterSpacing: "-0.02em",
                        }}
                    >
                        Download ArthGyan Today
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: 14, md: 16 },
                            opacity: 0.9,
                            maxWidth: 520,
                            mx: "auto",
                            mt: 1.5,
                            mb: 4,
                        }}
                    >
                        Smart finance, simplified. Start your journey with ArthGyan today.
                    </Typography>

                    {/* platform switch */}
                    <Stack
                        direction="row"
                        sx={{
                            position: "relative",
                            width: "fit-content",
                            mx: "auto",
                            mb: 3,
                            p: 0.6,
                            borderRadius: 999,
                            bgcolor: "rgba(255,255,255,0.16)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255,255,255,0.25)",
                        }}
                    >
                        {["android", "ios"].map((item) => {
                            const active = platform === item;
                            return (
                                <Button
                                    key={item}
                                    onClick={() => setPlatform(item)}
                                    disableRipple
                                    startIcon={
                                        item === "android" ? <AndroidIcon /> : <AppleIcon />
                                    }
                                    sx={{
                                        position: "relative",
                                        px: 3,
                                        py: 0.9,
                                        borderRadius: 999,
                                        textTransform: "none",
                                        fontWeight: 700,
                                        color: active ? primary : "#fff",
                                        transition: "color .25s ease",
                                        "&:hover": { bgcolor: "transparent" },
                                    }}
                                >
                                    {active && (
                                        <motion.span
                                            layoutId="platform-pill"
                                            transition={{
                                                type: "spring",
                                                stiffness: 400,
                                                damping: 32,
                                            }}
                                            style={{
                                                position: "absolute",
                                                inset: 0,
                                                borderRadius: 999,
                                                background: "#fff",
                                                boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
                                            }}
                                        />
                                    )}
                                    <Box component="span" sx={{ position: "relative", zIndex: 1 }}>
                                        {item === "android" ? "Android" : "iOS"}
                                    </Box>
                                </Button>
                            );
                        })}
                    </Stack>

                    {/* store button */}
                    <motion.div
                        whileHover={comingSoon ? undefined : { scale: 1.04, y: -2 }}
                        whileTap={comingSoon ? undefined : { scale: 0.98 }}
                        style={{ display: "inline-block" }}
                    >
                        <Button
                            disabled={comingSoon}
                            startIcon={isAndroid ? <AndroidIcon /> : <AppleIcon />}
                            onClick={() =>
                                window.open(storeUrl, "_blank", "noopener,noreferrer")
                            }
                            sx={{
                                px: 4,
                                py: 1.4,
                                borderRadius: 999,
                                fontWeight: 700,
                                fontSize: 16,
                                textTransform: "none",
                                bgcolor: "#0b0d12",
                                color: "#fff",
                                boxShadow: "0 14px 30px rgba(0,0,0,0.35)",
                                "&:hover": { bgcolor: "#000" },
                                "&.Mui-disabled": {
                                    bgcolor: "rgba(255,255,255,0.22)",
                                    color: "rgba(255,255,255,0.85)",
                                },
                            }}
                        >
                            {comingSoon
                                ? "iOS app coming soon"
                                : isAndroid
                                    ? "Get it on Play Store"
                                    : "Download on App Store"}
                        </Button>
                    </motion.div>
                </Box>
            </Box>
        </Box>
    );
};

export default AppPreview;
